import { ImageBlockModel, ImageBlockSchema } from '@blocksuite/affine-model';
import {
  FetchUtils,
  FULL_FILE_PATH_KEY,
  getImageFullPath,
} from '@blocksuite/affine-shared/adapters';
import { getFilenameFromContentDisposition } from '@blocksuite/affine-shared/utils';
import { sha } from '@blocksuite/global/utils';
import {
  type AssetsManager,
  type ASTWalkerContext,
  type BlockSnapshot,
  nanoid,
} from '@blocksuite/store';
import type { ImageBlockComponent } from '../image-block';
import type { ImageEdgelessBlockComponent } from '../image-edgeless-block';

// 🔧 图片元数据序列化工具
interface ImageMetadata {
  caption?: string;
  originalUrl?: string;
  contentLength?: number;
  contentType?: string;
}
async function getImageBlob(model: ImageBlockModel) {
  const sourceId = model.props.sourceId$.peek();
  if (!sourceId) return null;

  const doc = model.store;
  let blob = await doc.blobSync.get(sourceId);
  if (!blob) return null;

  if (!blob.type) {
    const buffer = await blob.arrayBuffer();
    const FileType = await import('file-type');
    const fileType = await FileType.fileTypeFromBuffer(buffer);

    blob = new Blob([buffer], { type: fileType?.mime });
  }

  if (!blob.type.startsWith('image/')) return null;

  return blob;
}

/**
 * 将元数据序列化到 markdown alt 文本中
 * 格式: "{\"caption\": \"...\", \"originalUrl\": \"...\"}"
 */
export function serializeImageMetadata(metadata: ImageMetadata): string {
  if (!metadata.caption && !metadata.originalUrl) {
    return '';
  }
  const jsonStr = JSON.stringify(metadata);
  return jsonStr;
}

/**
 * 从 markdown alt 文本中解析元数据
 * 返回: { metadata: {...}, rawAlt: string }
 */
export function deserializeImageMetadata(alt: string | null): {
  metadata: ImageMetadata | null;
  rawAlt: string;
} {
  if (!alt) {
    return { metadata: null, rawAlt: '' };
  }

  try {
    // 从 alt 中移除 <meta> 标签
    const rawAlt = alt.trim();
    const metadata = JSON.parse(rawAlt) as ImageMetadata;

    return { metadata, rawAlt };
  } catch (error) {
    console.warn('Failed to deserialize image metadata:', error);
    return { metadata: null, rawAlt: alt };
  }
}

/**
 * 判断 URL 是否为有效的远程 URL（避免相对路径被当作 originalUrl）
 */
export function isValidRemoteUrl(url: string | undefined): boolean {
  if (!url) return false;

  // 排除相对路径
  if (
    url.startsWith('assets/') ||
    url.startsWith('./') ||
    url.startsWith('../')
  ) {
    return false;
  }

  // 检查是否为绝对 URL
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export async function processImageNodeToBlock(
  imageURL: string,
  walkerContext: ASTWalkerContext<BlockSnapshot>,
  assets: AssetsManager,
  configs: Map<string, string>,
  template?: boolean | string,
  metadata?: {
    alt?: string | null;
    title?: string | null;
    originalUrl?: string;
  }
) {
  // 🔥 提前解析元数据（懒加载模式需要在创建占位 block 时使用）
  let finalCaption = '';
  let finalOriginalUrl: string | undefined;
  let imageSize = 0;
  let imageContentType = '';
  let urlToFetch = imageURL;

  const altString = metadata?.alt ?? null;
  const { metadata: parsedMeta } = deserializeImageMetadata(altString);
  console.log('aaaaparsedMeta', parsedMeta);
  if (parsedMeta) {
    finalCaption = parsedMeta.caption || '';
    finalOriginalUrl = parsedMeta.originalUrl;
    imageSize = parsedMeta.contentLength || 0;
    imageContentType = parsedMeta.contentType || '';
  } else {
    finalCaption = metadata?.title || metadata?.alt || '';
    finalOriginalUrl = metadata?.originalUrl;
  }
  const target = 1_000_000; // 1MB
  const sep = imageURL.includes('?') ? '&' : '?';
  if (
    imageURL.startsWith('https://wy-static.muset.net') ||
    imageSize > target
  ) {
    urlToFetch = `${imageURL}${sep}imageView2/0/w/900/format/jpg/q/60`;
  }

  const validOriginalUrl = isValidRemoteUrl(finalOriginalUrl)
    ? finalOriginalUrl
    : undefined;

  let blobId = '';
  if (template) {
    // When template is true, the image URL is the full path of the image file
    blobId = imageURL;
  } else if (!FetchUtils.fetchable(imageURL)) {
    const fullFilePath = configs.get(FULL_FILE_PATH_KEY);
    // When importing markdown file with assets in a zip file,
    // the image URL is the relative path of the image file in the zip file
    // If the full file path is provided, use it to get the image full path
    if (fullFilePath) {
      const decodedImageURL = decodeURIComponent(imageURL);
      const imageFullPath = getImageFullPath(fullFilePath, decodedImageURL);
      blobId = assets.getPathBlobIdMap().get(imageFullPath) ?? '';
    } else {
      const imageURLSplit = imageURL.split('/');
      while (imageURLSplit.length > 0) {
        const key = assets
          .getPathBlobIdMap()
          .get(decodeURIComponent(imageURLSplit.join('/')));
        if (key) {
          blobId = key;
          break;
        }
        imageURLSplit.shift();
      }
    }
  } else {
    // 🔥 懒加载模式：对于远端 URL，先创建占位 block，后异步 fetch
    const lazyMode = configs.get('lazy-image-load') === 'true';

    if (lazyMode) {
      // 生成 block id，创建占位 block（sourceId 为空）
      const blockId = nanoid();

      // 将异步 fetch 任务存入 uploadingAssetsMap（不使用 imageProxy，CDN 直接访问）
      assets?.uploadingAssetsMap.set(blockId, {
        fetchUrl: urlToFetch,
        mapInto: sourceId => ({ sourceId }),
      });

      console.log(
        `🔄 [Lazy Image] Created placeholder block: ${blockId} for URL: ${imageURL} ,${urlToFetch}`
      );

      // 创建占位 block，sourceId 为空，保留 originalUrl
      walkerContext
        .openNode(
          {
            type: 'block',
            id: blockId,
            flavour: ImageBlockSchema.model.flavour,
            props: {
              sourceId: '', // 占位，等待异步加载后回填
              caption: finalCaption,
              originalUrl: imageURL, // 保留原始 URL，用于异步加载和展示
              thumbnailUrl: urlToFetch,
              imageContentType: imageContentType,
              isCompressed: true,
            },
            children: [],
          },
          'children'
        )
        .closeNode();
      walkerContext.skipAllChildren();
      return;
    }

    // 原有同步 fetch 逻辑
    try {
      let blob: Blob | null = null;
      let fileName = '';

      // 🔥 策略 1：优先从 DOM 获取（避免重复下载）
      blob = await getImageFromDom(imageURL);
      if (blob) {
        console.log('✅ Image loaded from DOM (no network request needed)');
        fileName = (imageURL.split('/').at(-1) ?? 'image') + '.webp';
        blobId = await sha(await blob.arrayBuffer());
      } else {
        // 🔥 策略 2：DOM 中没有才通过网络下载
        console.log('⬇️ Image not in DOM, fetching from network...');

        const res = await FetchUtils.fetchImage(
          urlToFetch,
          undefined,
          configs.get('imageProxy') as string
        );

        if (!res) {
          console.error('Failed to fetch image from network');
          return;
        }

        const clonedRes = res.clone();
        fileName =
          getFilenameFromContentDisposition(
            res.headers.get('Content-Disposition') ?? ''
          ) ??
          (imageURL.split('/').at(-1) ?? 'image') +
            '.' +
            (res.headers.get('Content-Type')?.split('/').at(-1) ?? 'png');

        blob = await res.blob();
        blobId = await sha(await clonedRes.arrayBuffer());
      }

      if (!blob || !blobId) {
        console.error('Failed to process image: no blob or blobId');
        return;
      }

      const file = new File([blob], fileName, {
        type: blob.type || 'image/webp',
      });

      assets?.getAssets().set(blobId, file);
      await assets?.writeToBlob(blobId);
    } catch (err) {
      console.error('Failed to process image:', err);
      return;
    }
  }

  walkerContext
    .openNode(
      {
        type: 'block',
        id: nanoid(),
        flavour: ImageBlockSchema.model.flavour,
        props: {
          sourceId: blobId,
          caption: finalCaption,
          originalUrl: validOriginalUrl,
          imageContentType: imageContentType,
          thumbnailUrl: urlToFetch,
          isCompressed: true,
        },
        children: [],
      },
      'children'
    )
    .closeNode();
  walkerContext.skipAllChildren();
}

/**
 * 🔥 检查并压缩本地 blob 图片（如果超过 1MB）
 * 使用与 adapters/utils.ts 相同的压缩逻辑
 */
export async function maybeCompressLocalImageBlob(
  block: ImageBlockComponent | ImageEdgelessBlockComponent
) {
  const { model, store, blobUrl } = block;

  const originalUrl = model.props.originalUrl;

  // 0. 如果已经压缩过，直接返回
  if (model.props.isCompressed) return;

  // 1. 没有 sourceId 直接返回（说明要么是 CDN 占位，要么还没同步好）
  const sourceId = model.props.sourceId$.peek();
  if (!sourceId) return;

  // 2. 获取当前 blob
  const blob = await getImageBlob(model);
  if (!blob) return;
  if (!originalUrl) return;

  const target = 1_000_000; // 1MB
  const imageSize = blob.size;

  // 3. 如果已经小于等于目标大小，更新 size 属性并标记为已处理（不需要压缩）后返回
  if (imageSize <= target) {
    if (
      !model.props.size ||
      model.props.size !== imageSize ||
      !model.props.isCompressed
    ) {
      store.updateBlock(model, {
        size: imageSize,
        isCompressed: true, // 即使不需要压缩，也标记为已处理，避免重复检查
      });
    }
    return;
  }

  // 4. 计算压缩比例（沿用 adapters/utils.ts 中的公式）

  const ratio = Math.sqrt(target / imageSize);
  const sep = originalUrl?.includes('?') ? '&' : '?';
  const q = Math.min(100, Math.max(20, Math.round(ratio * 100))); // 质量范围 20-100// Canvas 需要 0-1 范围
  let urlToFetch = blobUrl;

  const objectUrl = URL.createObjectURL(blob);
  try {
    // JPEG 转换 + 尺寸限制 + 质量压缩
    urlToFetch = `${originalUrl}${sep}imageView2/0/w/900/format/jpg/q/60`;

    const res = await FetchUtils.fetchImage(urlToFetch, undefined, undefined);
    if (!res) {
      console.error('Failed to fetch image from network');
      return;
    }

    const newBlob = await res.blob();
    const newSourceId = await store.blobSync.set(
      new File([newBlob], 'image.jpg', { type: 'image/jpeg' })
    );
    // 8. 更新 block：使用新的 sourceId、size 和压缩标志
    store.updateBlock(model, {
      sourceId: newSourceId,
      size: newBlob.size,
      isCompressed: true, // 标记为已压缩
    });
  } catch (error) {
    console.error('Failed to compress local image blob:', error);
    // 压缩失败不影响正常使用，静默失败
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

// 修改 getImageFromDom 函数，使其更通用
async function getImageFromDom(url: string): Promise<Blob | null> {
  try {
    // 尝试从 DOM 中查找已加载的图片
    const img = document.querySelector<HTMLImageElement>(`img[src="${url}"]`);
    if (!img) {
      return null;
    }

    // 等待图片完全加载
    if (!img.complete) {
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        // 设置超时
        setTimeout(() => reject(new Error('Image load timeout')), 5000);
      });
    }

    // 检查图片尺寸是否有效
    if (img.naturalWidth === 0 || img.naturalHeight === 0) {
      return null;
    }

    // 🔥 关键修复：检测图片是否跨域
    // 如果图片跨域且没有 CORS 头，Canvas 会被污染，无法 toBlob
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    ctx.drawImage(img, 0, 0);

    // 🔥 尝试读取像素数据来检测是否被污染
    // 如果被污染，这一步会抛出 SecurityError
    try {
      ctx.getImageData(0, 0, 1, 1);
    } catch (securityError) {
      // Canvas 被污染，无法导出
      console.warn(
        'Image is tainted (CORS issue), cannot export from DOM:',
        url
      );
      return null; // 返回 null，让后续逻辑走 fetch 下载
    }

    // 统一导出 WebP，quality 设为 0.9
    const blob = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(resolve, 'image/webp', 0.9)
    );

    return blob;
  } catch (error) {
    console.warn('Failed to get image from dom:', error);
    return null; // 失败时返回 null，让后续走 fetch
  }
}
